import {
	type LoaderFunctionArgs,
	type MetaFunction,
	json,
} from "@remix-run/cloudflare";
import { useLoaderData } from "@remix-run/react";
import type { HTMLProps } from "react";
import { ChevronLeft, ChevronRight, Map as MapIcon } from "react-feather";
import { Icon } from "../components/Icon";
import { Layout } from "../components/Layout";
import { TravelHeader } from "../components/TravelHeader";
import { TravelPolaroid } from "../components/TravelPolaroid";
import {
	type TravelPost,
	getPosts,
	stripPostsForListing,
} from "../data/travelPosts";
import type { SitemapDataFunction } from "../lib/sitemapUtils";

type TravelPostPageProps = {
	country: string;
	town: string;
	post: TravelPost;
	nextPost: null | TravelPost;
	prevPost: null | TravelPost;
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	if (!data) throw new Error("No data");
	return [{ title: data.post.title }];
};

export default function TravelPostPage() {
	const { country, town, post, nextPost, prevPost } =
		useLoaderData<typeof loader>();

	const images = post.images.map((img) => ({
		...img,
		portrait: img.height > img.width,
		isPortraitPaired: false,
	}));

	// TODO: Extract
	let canLastBePaired = false;
	images.forEach((img, i) => {
		if (canLastBePaired) {
			const prevImg = images[i - 1];
			if (img.portrait && prevImg) {
				prevImg.isPortraitPaired = true;
				img.isPortraitPaired = true;
			}
			canLastBePaired = false;
		} else if (img.portrait) {
			canLastBePaired = true;
		}
	});

	return (
		<Layout>
			<main>
				<article>
					<TravelHeader countrySlug={country} townSlug={town} posts={[post]} />
					<div className="grid grid-cols-2 gap-10">
						{images.map((img, i) => {
							const colSpanClass = img.isPortraitPaired
								? "md:col-span-1"
								: "md:col-span-2";

							return (
								<figure
									key={img.src.large}
									className={`col-span-2 ${colSpanClass} mx-auto max-w-[1200px]`}
								>
									<img
										src={img.src.large}
										alt={img.alt}
										width={img.width}
										height={img.height}
										className={"block rounded bg-placeholder"}
										loading={i < 2 ? "eager" : "lazy"}
									/>
									<figcaption className="mt-2 flex justify-between">
										<div
											// biome-ignore lint/security/noDangerouslySetInnerHtml: This is safe because the caption is trusted HTML.
											dangerouslySetInnerHTML={{ __html: img.caption }}
											className="text-sm"
										/>
										{img.googleMapsURL && (
											<a
												href={img.googleMapsURL}
												target="_blank"
												rel="noopener noreferrer"
												title="Opens in a new tab"
												className="block text-sm"
											>
												{"View on map "}
												<Icon
													icon={MapIcon}
													className="top-[-0.1em] inline-block"
												/>
											</a>
										)}
									</figcaption>
								</figure>
							);
						})}
					</div>

					{/* TODO: These inside main? In footer? Inside main Article? Tidy up */}
					{(prevPost || nextPost) && (
						<aside className="mx-auto mt-20 max-w-[800px]">
							<h2 className="mb-10 text-center font-bold">Other places</h2>
							<div className="grid gap-10 px-10 md:grid-cols-2">
								{prevPost && (
									<RelatedPost
										post={prevPost}
										type="prev"
										className="order-2 md:order-1"
									/>
								)}
								{nextPost && (
									<RelatedPost
										post={nextPost}
										type="next"
										className="order-1 md:order-2"
									/>
								)}
							</div>
						</aside>
					)}
				</article>
			</main>
		</Layout>
	);
}

const RelatedPost = ({
	type,
	post,
	...rest
}: {
	type: "prev" | "next";
	post: TravelPost;
} & HTMLProps<HTMLElement>) => {
	const typeMeta =
		type === "prev"
			? {
					justifyContent: "flex-start",
					iconOrder: 0,
					title: "Previous",
					ChevronIcon: ChevronLeft,
					randomSeed: 3,
				}
			: {
					justifyContent: "flex-end",
					iconOrder: 1,
					title: "Next",
					ChevronIcon: ChevronRight,
					randomSeed: 4,
				};

	return (
		<TravelPolaroid
			{...rest}
			key={post.randomImage.src.medium}
			randomSeed={typeMeta.randomSeed}
			imageLoading="lazy"
			title={post.town}
			image={post.randomImage}
			to={post.pathname}
		/>
	);
};

export const loader = async ({ params }: LoaderFunctionArgs) => {
	const { country, town } = params;

	if (typeof country !== "string") {
		throw new Error("Expected a single `countrySlug`");
	}

	if (typeof town !== "string") {
		throw new Error("Expected a single `townSlug`");
	}

	const slugs = { countrySlug: country, townSlug: town };
	const [post] = getPosts(slugs);

	if (!post) {
		throw new Response("Not Found", {
			status: 404,
		});
	}

	// Get next / prev posts. It wraps around, so the first post
	// links to the last post, and vice versa.
	const allPosts = getPosts();

	const firstPost = allPosts[0] ?? null;
	const lastPost = allPosts[allPosts.length - 1] ?? null;

	const postIndex = allPosts.findIndex(
		({ pathname }) => pathname === post.pathname,
	);
	const prevPost = allPosts[postIndex - 1] ?? lastPost;
	const nextPost = allPosts[postIndex + 1] ?? firstPost;

	return json<TravelPostPageProps>({ country, town, post, prevPost, nextPost });
};

export const sitemap: SitemapDataFunction = () => {
	return stripPostsForListing(getPosts());
};
